import { useCallback, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { CreateDocumentRequestDto } from '@tiendanube/common';
import { AsyncDocumentType } from '@tiendanube/common/src/enums';
import { useAppDispatch } from 'App/store';
import { openWindow } from 'commons/utils/window';
import { createDocumentDownloadLinkAsync } from 'domains/FulfillmentOrders/fulfillmentOrdersSlice/fulfillmentOrdersSlice';
import { DocumentType } from '../../constants';
import useActionsToast from '../useActionsToast';
import useAsyncDocumentPolling from '../useAsyncDocumentPolling';

const DOCUMENT_TYPE_TO_API_TYPE: Record<string, AsyncDocumentType> = {
  [DocumentType.LABEL]: AsyncDocumentType.LABEL,
  [DocumentType.CONTENT_DECLARATION]: AsyncDocumentType.CONTENT_DECLARATION,
  [DocumentType.RECEIPT]: AsyncDocumentType.RECEIPT,
  [DocumentType.SHIPPING_DOCUMENT]: AsyncDocumentType.SHIPPING_DOCUMENT,
};

export interface HandleDownloadDocumentProps {
  type: DocumentType;
  params: CreateDocumentRequestDto;
}
export interface UseCreateDocumentsDownloadResult {
  handleDownloadDocumentAsync: (
    props: HandleDownloadDocumentProps,
  ) => Promise<void>;
  isGenerating: boolean;
}

function useCreateDocumentsDownload(): UseCreateDocumentsDownloadResult {
  const dispatch = useAppDispatch();
  const [jobId, setJobId] = useState<string | null>(null);
  const [isAsyncLoading, setIsAsyncLoading] = useState(false);

  const { onErrorPrintManager, onSuccessPrintManager, onTimeoutPrintManager } =
    useActionsToast();

  const handlePollingComplete = useCallback(
    (url: string) => {
      setIsAsyncLoading(false);
      onSuccessPrintManager();
      openWindow(url, true);
      setJobId(null);
    },
    [onSuccessPrintManager],
  );

  const handlePollingError = useCallback(() => {
    setIsAsyncLoading(false);
    setJobId(null);
  }, []);

  const handlePollingTimeout = useCallback(() => {
    setIsAsyncLoading(false);
    onTimeoutPrintManager();
    setJobId(null);
  }, [onTimeoutPrintManager]);

  const { isPolling } = useAsyncDocumentPolling(
    jobId,
    handlePollingComplete,
    handlePollingError,
    handlePollingTimeout,
  );

  const handleDownloadDocumentAsync = useCallback(
    async (props: HandleDownloadDocumentProps): Promise<void> => {
      const { type, params } = props;
      const apiType = DOCUMENT_TYPE_TO_API_TYPE[type];

      if (!apiType) {
        onErrorPrintManager();
      }

      try {
        setIsAsyncLoading(true);

        const jobResponse = unwrapResult(
          await dispatch(
            createDocumentDownloadLinkAsync({ type: apiType, params }),
          ),
        );

        setJobId(jobResponse.id);
      } catch (error) {
        setIsAsyncLoading(false);
        onErrorPrintManager();
      }
    },
    [dispatch, onErrorPrintManager],
  );

  return {
    handleDownloadDocumentAsync,
    isGenerating: isAsyncLoading || isPolling,
  };
}

export default useCreateDocumentsDownload;
